import { Injectable, inject } from '@angular/core';
import { getDocumentDict } from '@documents/selectors/documents.selectors';
import { DocumentsService } from '@documents/services/documents.service';
import { Store } from '@ngrx/store';

import {
  catchError,
  combineLatest,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { MessagesService } from './messages.service';
import { UtilsService } from '@shared/services/utils.service';

import {
  IMessageReqBody,
  IMessageResponse,
  MessageEntityType,
  ThreadType,
} from '@data-access/submissions/types';
import * as DocumentActions from '@documents/actions/documents.actions';
import * as QuestionnaireActions from '@submissions/v1/redux/questionnaire.actions';
import { toSignal } from '@angular/core/rxjs-interop';
import { IAnswerSchema } from '../models';
import { extractBlobAndFilename, saveAs } from '@shared/utils/download.util';
import { HttpResponse } from '@angular/common/http';
import {
  clearThreadList,
  closeActiveThread,
  findMessageFromEntity,
  reloadThread,
  replytoThread,
} from '../redux/submission.actions';
import {
  selectActiveSubmissionId,
  selectActiveSubmissionStage,
  selectThread,
} from '../redux/submission.selectors';
import { selectQuestionDict } from '../redux/questionnaire.selectors';

@Injectable({
  providedIn: 'root',
})
export class MessagesDetailsFacadeService {
  private readonly _store = inject(Store);
  private readonly _utilsService = inject(UtilsService);
  private readonly _messageService = inject(MessagesService);
  private readonly _documentService = inject(DocumentsService);

  currentMessage$ = new Subject<{
    submissionId?: string;
    message?: IMessageReqBody;
  }>();

  messageData$ = this.currentMessage$.pipe(
    filter((message) => !!message.message && !!message.submissionId),
    switchMap(({ submissionId, message }) =>
      this._messageService.createMessage(submissionId!, message!).pipe(
        map((res: IMessageResponse) => {
          const body = {
            ...this._utilsService.convertKeysToCamelCase(res),
            isResolved: false,
            respond: true,
          };

          this._store.dispatch(closeActiveThread());
          this._store.dispatch(
            QuestionnaireActions.updateAnswerThroughSocket({
              answer: res as IAnswerSchema,
            }),
          );
          if (body.entityType === MessageEntityType.DOCUMENT) {
            this._store.dispatch(
              DocumentActions.updateThreadtoEntityDocument({
                entityId: message?.workspace_id ?? '',
                entityType: 'workspace',
                isResolved: false,
                threadId: body.entityId,
              }),
            );
          }
        }),
        catchError((error) => {
          console.error('Error creating message:', error);
          return of(null);
        }),
      ),
    ),
  );

  replyMessage$ = new Subject<{
    submissionId?: string;
    messageId?: string;
    message?: IMessageReqBody;
  }>();

  replyMessageData$ = this.replyMessage$.pipe(
    filter(
      (message) =>
        !!message.message && !!message.submissionId && !!message.messageId,
    ),
    switchMap(({ submissionId, messageId, message }) =>
      this._messageService
        .replyMessage(submissionId!, messageId!, message!)
        .pipe(
          map((res: any) => this._utilsService.convertKeysToCamelCase(res)),
          // TODO: Try without tap and use map
          tap((res: any) => {
            this._store.dispatch(
              replytoThread({
                message: res,
              }),
            );
            if (message?.entity_type === MessageEntityType.ANSWER) {
              this._store.dispatch(
                QuestionnaireActions.updateAnswerThroughSocket({
                  answer: res,
                }),
              );
            }
            if (message?.entity_type === MessageEntityType.DOCUMENT)
              this._store.dispatch(
                DocumentActions.updateThreadtoEntityDocument({
                  entityId: message.workspace_id,
                  entityType: 'workspace',
                  isResolved: !message.response_required,
                  threadId: message.entity_id,
                }),
              );
          }),

          catchError((error) => {
            console.error('Error replying message:', error);
            return of(null);
          }),
        ),
    ),
  );

  getCollabhub$: Observable<any> = combineLatest([
    this._store.select(selectActiveSubmissionId),
    this._store.select(selectActiveSubmissionStage),
  ]).pipe(
    filter(
      (submissionId, stage) =>
        submissionId !== undefined && stage !== undefined,
    ),
    map(([submissionId]) => {
      this._messageService.getMessages(submissionId ?? '');
      this._documentService.loadEntityDocuments(
        'workspace',
        submissionId ?? '',
      );
    }),
  );
  selectThreadEntityType = new Subject<{
    threadId: string;
    threadType: ThreadType;
  } | null>();

  allMessages$: Observable<any> = this._messageService.messages$.pipe(
    mergeMap((messages) => {
      return of(messages);
    }),
  );

  allHistoryMessages$: Observable<any> =
    this._messageService.messagesHistory$.pipe(
      mergeMap((messages) => {
        return of(messages);
      }),
    );

  activeThread$: Observable<any> = this._messageService.activeThread$.pipe(
    mergeMap((thread) => {
      return of(thread);
    }),
  );
  $activeThread = toSignal(this._store.select(selectThread));

  selectMessageThread(threadId: string) {
    this._store.dispatch(reloadThread({ threadId }));
  }

  closeMessageThread() {
    this._store.dispatch(closeActiveThread());
  }

  clearMessages() {
    this.closeMessageThread();
    this._store.dispatch(clearThreadList());
  }

  getTitleFromDict(id: string): Observable<string> {
    const firstDictionary$ = this._store.select(selectQuestionDict);
    const secondDictionary$ = this._store.select(getDocumentDict);

    return combineLatest([firstDictionary$, secondDictionary$]).pipe(
      map(([firstDict, secondDict]) => {
        if (firstDict && firstDict[id]) {
          return firstDict[id].title;
        } else if (secondDict && secondDict[id]) {
          return secondDict[id].title;
        }
        return '';
      }),
    );
  }

  getResolvedDocumentName(id: string) {
    const documentDict$ = this._store.select(getDocumentDict);
    return documentDict$.pipe(
      map((documentDict) => {
        if (documentDict && documentDict[id]) {
          return documentDict[id].title;
        }
        return '';
      }),
    );
  }

  getDocumentTypeFromDict(id: string): Observable<string> {
    return this._store.select(getDocumentDict).pipe(
      filter((dict) => !!dict && !!dict[id]),
      map((dict) => dict[id].documentType),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  retrieveMessageFromEntity(entityID: string) {
    this._store.dispatch(findMessageFromEntity({ entityID }));
  }

  refreshThread(threadId: string) {
    this._store.dispatch(reloadThread({ threadId }));
  }

  selectThreadFromQuestion(
    questionId: string,
    workspaceId: string,
    entityId: ThreadType,
  ) {
    this._messageService.selectThread(questionId, workspaceId, entityId);
  }

  exportSingleChat(submissionId: string, entityId: string): void {
    this._exportFile(
      this._messageService.exportSingleChat(submissionId, entityId),
    );
  }

  exportChat(submissionId: string): void {
    this._exportFile(this._messageService.exportChat(submissionId));
  }

  private _exportFile(obs: Observable<HttpResponse<Blob>>): void {
    obs.subscribe({
      next: (res) => {
        const extracted = extractBlobAndFilename(res);
        const { blob, filename } = extracted;
        saveAs(blob, filename);
      },
    });
  }
}
